<template>
  <main class="runtime-monitor-page">
    <div class="monitor-toolbar" aria-label="系统监控控制">
      <div class="health-summary">
        <button
          class="count-button"
          :aria-pressed="store.serviceFilter.status === 'abnormal'"
          @click="store.serviceFilter.status = 'abnormal'"
        >
          异常 <strong>{{ store.abnormalCount }}</strong></button
        ><button
          v-if="serviceDiagnostics"
          class="count-button"
          :aria-pressed="store.serviceFilter.status === 'unknown'"
          @click="store.serviceFilter.status = 'unknown'"
        >
          未验证 <strong>{{ store.unknownCount }}</strong></button
        ><span class="sample-time">采样 {{ formatTime(store.overview?.generatedAt) }}</span>
      </div>
      <a-space wrap
        ><label class="refresh-control"
          ><a-switch v-model:checked="store.autoRefresh" size="small" aria-label="自动刷新监控" />
          自动刷新</label
        ><a-button :loading="store.overviewLoading" @click="store.refresh"
          ><i class="ri-refresh-line" aria-hidden="true" /> 刷新</a-button
        ></a-space
      >
    </div>
    <RuntimeMetrics v-if="!serviceDiagnostics" />
    <OnlineUsers v-if="!serviceDiagnostics" />
    <ServiceHealthTable :errors-only="!serviceDiagnostics" @open-queue="showQueue" />
    <TaskDiagnostics v-if="!serviceDiagnostics" />
    <DiagnosticDrawer :allow-configuration="serviceDiagnostics" @open-queue="showQueue" />
  </main>
</template>
<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAdminRuntimeMonitorStore } from '@/stores/admin-runtime-monitor.store';
import ServiceHealthTable from '@/components/monitor/ServiceHealthTable.vue';
import TaskDiagnostics from '@/components/monitor/TaskDiagnostics.vue';
import DiagnosticDrawer from '@/components/monitor/DiagnosticDrawer.vue';
import RuntimeMetrics from '@/components/monitor/RuntimeMetrics.vue';
import OnlineUsers from '@/components/monitor/OnlineUsers.vue';
import { formatTime } from '@/components/monitor/monitor-format';
const props = withDefaults(defineProps<{ serviceDiagnostics?: boolean }>(), {
  serviceDiagnostics: false,
});
const store = useAdminRuntimeMonitorStore();
const route = useRoute();
const router = useRouter();
watch(
  () => [props.serviceDiagnostics, route.query.taskId, route.query.queueId],
  () => {
    store.activatePage(props.serviceDiagnostics ? 'services' : 'monitor');
    const taskId = route.query.taskId;
    const queueId = route.query.queueId;
    if (
      !props.serviceDiagnostics &&
      typeof taskId === 'string' &&
      /^[A-Za-z0-9_.:-]{1,128}$/.test(taskId)
    ) {
      store.tab = 'agent';
      store.queries.agent.search = taskId;
    } else if (
      !props.serviceDiagnostics &&
      typeof queueId === 'string' &&
      /^[A-Za-z0-9-]{1,128}$/.test(queueId)
    ) {
      store.openQueue(queueId);
      void nextTick(scrollToTasks);
    }
    void store.refresh();
  },
  { immediate: true },
);
function showQueue(id: string): void {
  if (props.serviceDiagnostics) {
    void router.push({ name: 'admin-monitor', query: { queueId: id } });
    return;
  }
  store.openQueue(id);
  store.closeDetail();
  void nextTick(scrollToTasks);
}
function scrollToTasks(): void {
  const heading = document.getElementById('task-heading');
  heading?.scrollIntoView({ block: 'start' });
  heading?.focus({ preventScroll: true });
}
let interval: ReturnType<typeof setInterval> | undefined;
let onlineInterval: ReturnType<typeof setInterval> | undefined;
function refreshVisible(): void {
  if (store.autoRefresh && document.visibilityState === 'visible') void store.refresh();
}
onMounted(() => {
  interval = setInterval(refreshVisible, 15_000);
  onlineInterval = setInterval(() => {
    if (!props.serviceDiagnostics && store.autoRefresh && document.visibilityState === 'visible')
      void store.loadOnlineUsers();
  }, 5_000);
  window.addEventListener('focus', refreshVisible);
  document.addEventListener('visibilitychange', refreshVisible);
});
onBeforeUnmount(() => {
  if (interval) clearInterval(interval);
  if (onlineInterval) clearInterval(onlineInterval);
  window.removeEventListener('focus', refreshVisible);
  document.removeEventListener('visibilitychange', refreshVisible);
  store.closeDetail();
});
</script>
<style scoped src="../components/monitor/monitor-page.css"></style>
